import React from 'react';
import { FaCreditCard, FaDownload, FaExclamationCircle, FaInfoCircle } from 'react-icons/fa';

export type PaymentSectionId =
  | 'payments-tab'
  | 'downloadable-reports'
  | 'order-errors-adjustments'
  | 'important-notes';

export const PAYMENTS_NAV_ITEMS: { id: PaymentSectionId; label: string }[] = [
  { id: 'payments-tab', label: 'How customers pay' },
  { id: 'downloadable-reports', label: 'Downloadable reports' },
  { id: 'order-errors-adjustments', label: 'Order errors & adjustments' },
  { id: 'important-notes', label: 'Important notes & access' },
];

interface PaymentsInfoPanelProps {
  activeSection?: PaymentSectionId;
}

export default function PaymentsInfoPanel({
  activeSection = 'payments-tab',
}: PaymentsInfoPanelProps) {
  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600 max-w-3xl">
        Payment handling in QahwaHub today: customers can pay with{' '}
        <span className="font-semibold">card</span>,{' '}
        <span className="font-semibold">Google Pay</span>,{' '}
        <span className="font-semibold">Apple Pay</span>, or{' '}
        <span className="font-semibold">cash</span>. Staff reconcile using{' '}
        <span className="font-semibold">Reports</span> Excel exports and the{' '}
        <span className="font-semibold">Issues</span> workflow when refunds apply.
      </p>

      {activeSection === 'payments-tab' && (
        <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
          <h2 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
            <FaCreditCard className="text-amber-700" />
            How customers pay
          </h2>
          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <p>
              <span className="font-semibold text-gray-900">Card / wallets:</span> Customers
              choose Card in the app, then pay with Visa/Mastercard, Google Pay, or Apple Pay
              on the NI hosted page. Cash remains available in store.
            </p>
            <p>
              <span className="font-semibold text-gray-900">Cash:</span> Customer selects cash at
              checkout; staff complete the order in the dashboard when paid in store.
            </p>
            <p>
              <span className="font-semibold text-gray-900">Wallet &amp; points:</span> Approved
              issue refunds can credit the customer wallet (used on later orders). Points and
              promotions reduce the total before payment; the stored{' '}
              <span className="font-semibold">total_amount</span> is what was charged or recorded
              for the order.
            </p>
            <p className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-amber-950">
              There is no automated weekly bank payout in this dashboard yet. Use{' '}
              <span className="font-semibold">Reports → Payment details</span> for per-order
              breakdowns in the period you select.
            </p>
          </div>
        </section>
      )}

      {activeSection === 'downloadable-reports' && (
        <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
          <h2 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
            <FaDownload className="text-amber-700" />
            Downloadable reports
          </h2>
          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <p>
              Open the <span className="font-semibold">Reports</span> section in the sidebar (same
              café filter and period as other exports).
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <span className="font-semibold">Order history</span> — all orders in the period
                with status, total, and line items.
              </li>
              <li>
                <span className="font-semibold">Payment details</span> — completed orders with
                payment method, wallet/points/promotion amounts, and totals (JOD).
              </li>
              <li>
                <span className="font-semibold">Order errors &amp; inaccurate orders</span> —
                canceled orders for refund review.
              </li>
              <li>
                <span className="font-semibold">Issue reports</span> (support admin) — customer
                post-delivery issues and refund outcomes.
              </li>
            </ul>
          </div>
        </section>
      )}

      {activeSection === 'order-errors-adjustments' && (
        <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
          <h2 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
            <FaExclamationCircle className="text-amber-700" />
            Order errors &amp; adjustments
          </h2>
          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <p>
              Customer <span className="font-semibold">Issue reports</span> (wrong/missing items,
              etc.) are reviewed under <span className="font-semibold">Issues</span> in the sidebar.
              Refunds to wallet are applied when support updates the issue outcome.
            </p>
            <p>
              Canceled orders appear in <span className="font-semibold">Reports → Order errors</span>
              . Cash orders have different refund options than card orders in the customer issue
              flow.
            </p>
            <p>
              Payout adjustment columns in exports are placeholders until café payout
              reconciliation is built on the server.
            </p>
          </div>
        </section>
      )}

      {activeSection === 'important-notes' && (
        <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
          <h2 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
            <FaInfoCircle className="text-amber-700" />
            Important notes
          </h2>
          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <p>
              <span className="font-semibold">Café scope:</span> Baristas see their assigned café;
              superusers can switch location in the sidebar.
            </p>
            <p>
              <span className="font-semibold">Service fee:</span> A small per-order service fee is
              included in checkout totals on the customer app (see order{' '}
              <span className="font-semibold">total_amount</span> in exports).
            </p>
            <p>
              <span className="font-semibold">Online gateways:</span> Production card payments and
              gateway-level settlement exports will be added after payment gateway integration is
              re-enabled on the live API. Until then, do not treat card totals as final bank settlement.
            </p>
            <p>
              <span className="font-semibold">Marketing spend</span> is tracked separately under
              Reports (campaign planner fees), not in payment details.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
